/**
 * useSpawnSessions Hook
 *
 * Provides data fetching and mutations for spawn sessions and related operations.
 * Part of US-PE-004: Spawn Sessions & Inventory
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/supabase-client'

/**
 * Hook to fetch all spawn sessions with their items
 * @param {Object} options - Filter options
 * @param {string} options.status - Filter by status (scheduled, in_progress, completed, cancelled)
 * @param {number} options.limit - Limit results
 * @returns {Object} { sessions, loading, error, refetch }
 */
export function useSpawnSessions(options = {}) {
  const [sessions, setSessions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchSessions = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      let query = supabase
        .from('spawn_sessions')
        .select(`
          *,
          items:spawn_session_items(
            *,
            strain:strains(id, code, name, origin),
            liquid_culture:liquid_cultures(id, culture_code, status)
          )
        `)
        .order('scheduled_date', { ascending: false })

      if (options.status) {
        query = query.eq('status', options.status)
      }

      if (options.limit) {
        query = query.limit(options.limit)
      }

      const { data, error: fetchError } = await query

      if (fetchError) {
        throw fetchError
      }

      setSessions(data || [])
    } catch (err) {
      console.error('[useSpawnSessions] Error:', err)
      setError(err.message)
      setSessions([])
    } finally {
      setLoading(false)
    }
  }, [options.status, options.limit])

  useEffect(() => {
    fetchSessions()
  }, [fetchSessions])

  return {
    sessions,
    loading,
    error,
    refetch: fetchSessions
  }
}

/**
 * Hook to fetch a single spawn session by ID with full details
 * @param {string} id - The spawn session UUID
 * @returns {Object} { session, loading, error, refetch }
 */
export function useSpawnSession(id) {
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchSession = useCallback(async () => {
    if (!id) {
      setSession(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { data, error: fetchError } = await supabase
        .from('spawn_sessions')
        .select(`
          *,
          items:spawn_session_items(
            *,
            strain:strains(id, code, name, origin),
            liquid_culture:liquid_cultures(id, culture_code, status, volume_remaining_ml)
          ),
          inventory:spawn_inventory(
            id,
            code,
            status,
            strain_id,
            colonisation_percent,
            inoculated_at,
            colonisation_complete_at
          )
        `)
        .eq('id', id)
        .single()

      if (fetchError) {
        throw fetchError
      }

      setSession(data)
    } catch (err) {
      console.error('[useSpawnSession] Error:', err)
      setError(err.message)
      setSession(null)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    fetchSession()
  }, [fetchSession])

  return {
    session,
    loading,
    error,
    refetch: fetchSession
  }
}

/**
 * Hook for spawn session mutations
 * @returns {Object} Mutation functions and state
 */
export function useSpawnSessionMutations() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Generate a spawn session from shadow batch requirements
   * Uses the generate_spawn_session database function
   */
  const generateSession = useCallback(async (sessionDate, weeksAhead = 2, safetyMarginPercent = 20) => {
    setLoading(true)
    setError(null)

    try {
      const { data, error: rpcError } = await supabase
        .rpc('generate_spawn_session', {
          p_session_date: sessionDate,
          p_weeks_ahead: weeksAhead,
          p_safety_margin_percent: safetyMarginPercent
        })

      if (rpcError) {
        throw rpcError
      }

      return data // Returns session UUID or null
    } catch (err) {
      console.error('[useSpawnSessionMutations.generate] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Create a spawn session manually
   */
  const createSession = useCallback(async (sessionData) => {
    setLoading(true)
    setError(null)

    try {
      const { data, error: insertError } = await supabase
        .from('spawn_sessions')
        .insert({
          code: sessionData.code,
          name: sessionData.name,
          scheduled_date: sessionData.scheduledDate,
          status: 'scheduled',
          notes: sessionData.notes
        })
        .select()
        .single()

      if (insertError) {
        throw insertError
      }

      return data
    } catch (err) {
      console.error('[useSpawnSessionMutations.create] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Update session status
   */
  const updateSessionStatus = useCallback(async (sessionId, status) => {
    setLoading(true)
    setError(null)

    try {
      const updates = { status }

      if (status === 'in_progress') {
        updates.started_at = new Date().toISOString()
      } else if (status === 'completed') {
        updates.completed_at = new Date().toISOString()
      }

      const { data, error: updateError } = await supabase
        .from('spawn_sessions')
        .update(updates)
        .eq('id', sessionId)
        .select()
        .single()

      if (updateError) {
        throw updateError
      }

      return data
    } catch (err) {
      console.error('[useSpawnSessionMutations.updateStatus] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Add an item to a spawn session
   */
  const addSessionItem = useCallback(async (sessionId, itemData) => {
    setLoading(true)
    setError(null)

    try {
      const { data, error: insertError } = await supabase
        .from('spawn_session_items')
        .insert({
          spawn_session_id: sessionId,
          strain_id: itemData.strainId,
          liquid_culture_id: itemData.liquidCultureId,
          container_type: itemData.containerType || 'bag',
          container_size: itemData.containerSize,
          grain_type: itemData.grainType,
          quantity: itemData.quantity,
          linked_batch_ids: itemData.linkedBatchIds,
          safety_margin_qty: itemData.safetyMarginQty || 0
        })
        .select(`
          *,
          strain:strains(id, code, name)
        `)
        .single()

      if (insertError) {
        throw insertError
      }

      return data
    } catch (err) {
      console.error('[useSpawnSessionMutations.addItem] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Update session item completion
   */
  const updateItemCompletion = useCallback(async (itemId, completedQty, contaminatedQty = 0) => {
    setLoading(true)
    setError(null)

    try {
      const { data, error: updateError } = await supabase
        .from('spawn_session_items')
        .update({
          quantity_completed: completedQty,
          quantity_contaminated: contaminatedQty
        })
        .eq('id', itemId)
        .select()
        .single()

      if (updateError) {
        throw updateError
      }

      return data
    } catch (err) {
      console.error('[useSpawnSessionMutations.updateCompletion] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Complete session and create inventory items
   */
  const completeSession = useCallback(async (sessionId, inoculatedAt = null) => {
    setLoading(true)
    setError(null)

    try {
      const { data, error: rpcError } = await supabase
        .rpc('complete_spawn_session', {
          p_session_id: sessionId,
          p_inoculated_at: inoculatedAt || new Date().toISOString()
        })

      if (rpcError) {
        throw rpcError
      }

      return data // Returns count of inventory items created
    } catch (err) {
      console.error('[useSpawnSessionMutations.complete] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    loading,
    error,
    generateSession,
    createSession,
    updateSessionStatus,
    addSessionItem,
    updateItemCompletion,
    completeSession
  }
}

/**
 * Hook to fetch spawn demand vs supply data
 * @returns {Object} { demandSupply, loading, error, refetch }
 */
export function useSpawnDemandSupply() {
  const [demandSupply, setDemandSupply] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchDemandSupply = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const { data, error: fetchError } = await supabase
        .from('spawn_demand_supply')
        .select('*')

      if (fetchError) {
        throw fetchError
      }

      setDemandSupply(data || [])
    } catch (err) {
      console.error('[useSpawnDemandSupply] Error:', err)
      setError(err.message)
      setDemandSupply([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchDemandSupply()
  }, [fetchDemandSupply])

  return {
    demandSupply,
    loading,
    error,
    refetch: fetchDemandSupply
  }
}

/**
 * Hook to fetch planning recommendations
 * @returns {Object} { recommendations, loading, error, refetch }
 */
export function useSpawnPlanningRecommendations() {
  const [recommendations, setRecommendations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchRecommendations = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const { data, error: fetchError } = await supabase
        .from('spawn_planning_recommendations')
        .select('*')

      if (fetchError) {
        throw fetchError
      }

      setRecommendations(data || [])
    } catch (err) {
      console.error('[useSpawnPlanningRecommendations] Error:', err)
      setError(err.message)
      setRecommendations([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchRecommendations()
  }, [fetchRecommendations])

  return {
    recommendations,
    loading,
    error,
    refetch: fetchRecommendations
  }
}

export default useSpawnSessions
